import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertCircle, CheckCircle2, Search, Loader, FileDown, Eye, ExternalLink, Download, ChevronRight } from 'lucide-react';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { toast } from 'sonner';
import { CurrencyDisplay } from '../components/DirhamSymbol';

// Shared react-select styles
const customStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#a3e635' : '#e2e8f0',
    borderRadius: '0.375rem',
    backgroundColor: 'white',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(163, 230, 53, 0.3)' : 'none',
    '&:hover': { borderColor: '#a3e635' },
    cursor: 'pointer'
  }),
  menu: (base) => ({
    ...base, zIndex: 99999, borderRadius: '0.375rem', border: '1px solid #e2e8f0',
    backgroundColor: 'white', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', marginTop: '4px', position: 'absolute'
  }),
  menuList: (base) => ({ ...base, maxHeight: '300px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '4px' }),
  option: (base, state) => ({
    ...base, backgroundColor: state.isFocused ? '#f1f5f9' : state.isSelected ? '#e2e8f0' : 'transparent',
    color: '#1e293b', cursor: 'pointer', borderRadius: '0.25rem', padding: '8px 12px', '&:active': { backgroundColor: '#e2e8f0' }
  }),
  input: (base) => ({ ...base, color: '#1e293b', margin: 0, padding: 0 }),
  placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  singleValue: (base) => ({ ...base, color: '#1e293b' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base, state) => ({
    ...base, color: '#94a3b8', padding: '8px', transition: 'transform 0.2s',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)', '&:hover': { color: '#64748b' }
  }),
  clearIndicator: (base) => ({ ...base, color: '#94a3b8', padding: '8px', '&:hover': { color: '#ef4444' } }),
  noOptionsMessage: (base) => ({ ...base, color: '#94a3b8', padding: '24px 16px', textAlign: 'center' }),
  valueContainer: (base) => ({ ...base, padding: '2px 8px' })
};

const formatOptionLabel = (option, { context }) => {
  if (context === 'value') return <span className="truncate">{option.label}</span>;
  return (
    <div className="flex flex-col gap-1 py-0.5">
      <span className="font-medium text-sm">{option.label}</span>
      {option.metadata && (
        <div className="flex flex-wrap gap-1">
          {Object.entries(option.metadata).map(([key, val]) => (
            val && <span key={key} className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{key}: {val}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
};

const SearchableDropdownDemo = () => {
  const [lessees, setLessees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [riderInfo, setRiderInfo] = useState(null);
  const [motorcycleInfo, setMotorcycleInfo] = useState(null);
  const [lesseeInfo, setLesseeInfo] = useState(null);

  const [selectedRider, setSelectedRider] = useState(null);
  const [selectedMotorcycle, setSelectedMotorcycle] = useState(null);
  const [selectedLessee, setSelectedLessee] = useState('');

  // Extended data for detail panels
  const [motorcycleHistory, setMotorcycleHistory] = useState(null);
  const [loadingMotorcycleHistory, setLoadingMotorcycleHistory] = useState(false);
  const [riderVehicleHistory, setRiderVehicleHistory] = useState(null);
  const [riderFinesHistory, setRiderFinesHistory] = useState(null);
  const [riderDocuments, setRiderDocuments] = useState(null);
  const [loadingRiderDetails, setLoadingRiderDetails] = useState(false);

  useEffect(() => {
    fetchLessees();
  }, []);

  const fetchLessees = async () => {
    try {
      const res = await axios.get(`${API}/lessees`, { headers: getAuthHeader() });
      setLessees(res.data);
    } catch (error) {
      console.error('Failed to fetch lessees:', error);
    } finally {
      setLoading(false);
    }
  };

  // Server-side async search for riders
  const loadRiderOptions = useCallback(async (inputValue) => {
    try {
      const params = new URLSearchParams({ limit: '50', skip: '0' });
      if (inputValue) params.append('search', inputValue);
      const res = await axios.get(`${API}/riders?${params.toString()}`, { headers: getAuthHeader() });
      return res.data.map(rider => ({
        value: rider.id,
        label: rider.name,
        metadata: {
          'Emirates ID': rider.emirates_id || '-',
          'Mobile': rider.mobile_uae || '-',
          'Lessee': rider.lessee_name || 'Not Assigned',
          'Bike': rider.assigned_bike_plate || rider.bike_plate_number || 'Not Assigned'
        },
        _raw: rider
      }));
    } catch (error) {
      console.error('Rider search failed:', error);
      return [];
    }
  }, []);

  // Server-side async search for motorcycles
  const loadMotorcycleOptions = useCallback(async (inputValue) => {
    try {
      const params = new URLSearchParams({ limit: '50', skip: '0' });
      if (inputValue) params.append('search', inputValue);
      const res = await axios.get(`${API}/motorcycles?${params.toString()}`, { headers: getAuthHeader() });
      return res.data.map(bike => ({
        value: bike.id,
        label: bike.plate_number,
        metadata: {
          'Model': bike.model_name,
          'Chassis': bike.chassis_number,
          'Status': bike.status,
          'Rider': bike.assigned_rider_name || bike.rider_name || 'Unassigned',
          'Lessee': bike.lessee_name || '-'
        },
        _raw: bike
      }));
    } catch (error) {
      console.error('Motorcycle search failed:', error);
      return [];
    }
  }, []);

  const lesseeOptions = lessees.map(lessee => ({
    value: lessee.id,
    label: lessee.company_name,
    searchText: `${lessee.company_name} ${lessee.owner_name} ${lessee.owner_mobile || ''} ${lessee.trade_license_number || ''} ${lessee.trn_number || ''}`,
    metadata: {
      'Owner': lessee.owner_name,
      'Mobile': lessee.owner_mobile || lessee.contact_mobile,
      'Trade License': lessee.trade_license_number || lessee.trade_license_no,
      'TRN': lessee.trn_number || lessee.trn_no
    }
  }));

  // Fetch motorcycle history when a motorcycle is selected
  const fetchMotorcycleHistory = async (motorcycleId) => {
    setLoadingMotorcycleHistory(true);
    try {
      const res = await axios.get(`${API}/motorcycles/${motorcycleId}/history`, { headers: getAuthHeader() });
      setMotorcycleHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch motorcycle history:', error);
      setMotorcycleHistory(null);
    } finally {
      setLoadingMotorcycleHistory(false);
    }
  };

  // Fetch rider details when a rider is selected
  const fetchRiderDetails = async (riderId) => {
    setLoadingRiderDetails(true);
    try {
      const [vehicleRes, finesRes, docsRes] = await Promise.allSettled([
        axios.get(`${API}/riders/${riderId}/vehicle-history`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/${riderId}/fines-history`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/${riderId}/documents`, { headers: getAuthHeader() })
      ]);
      setRiderVehicleHistory(vehicleRes.status === 'fulfilled' ? vehicleRes.value.data : null);
      setRiderFinesHistory(finesRes.status === 'fulfilled' ? finesRes.value.data : null);
      setRiderDocuments(docsRes.status === 'fulfilled' ? docsRes.value.data : null);
    } catch (error) {
      console.error('Failed to fetch rider details:', error);
    } finally {
      setLoadingRiderDetails(false);
    }
  };

  const handleRiderChange = (selected) => {
    setSelectedRider(selected);
    setRiderInfo(selected?._raw || null);
    setRiderVehicleHistory(null);
    setRiderFinesHistory(null);
    setRiderDocuments(null);
    if (selected?._raw?.id) {
      fetchRiderDetails(selected._raw.id);
    }
  };

  const handleMotorcycleChange = (selected) => {
    setSelectedMotorcycle(selected);
    setMotorcycleInfo(selected?._raw || null);
    setMotorcycleHistory(null);
    if (selected?._raw?.id) {
      fetchMotorcycleHistory(selected._raw.id);
    }
  };

  const handleLesseeChange = (lesseeId) => {
    setSelectedLessee(lesseeId);
    const lessee = lessees.find(l => l.id === lesseeId);
    setLesseeInfo(lessee || null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader className="w-6 h-6 animate-spin text-lime-600 mr-2" />
        <span className="text-slate-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800" data-testid="search-page-title">Search</h1>
        <p className="text-slate-600 mt-2">
          Quickly search for riders, motorcycles, and lessees by typing any related information.
          View complete details for any item in the system.
        </p>
      </div>

      {/* Feature Highlights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-lime-200 bg-lime-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-lime-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-800">Quick Search</h3>
                <p className="text-sm text-slate-600 mt-1">Find any rider, motorcycle, or lessee instantly</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-800">Search Anything</h3>
                <p className="text-sm text-slate-600 mt-1">Type name, ID, plate number, mobile, or any detail</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-800">Complete Details</h3>
                <p className="text-sm text-slate-600 mt-1">View all information about the selected item</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="rider" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="rider" data-testid="search-tab-rider">Search Riders</TabsTrigger>
          <TabsTrigger value="motorcycle" data-testid="search-tab-motorcycle">Search Motorcycles</TabsTrigger>
          <TabsTrigger value="lessee" data-testid="search-tab-lessee">Search Lessees</TabsTrigger>
        </TabsList>

        {/* ===== RIDERS TAB ===== */}
        <TabsContent value="rider">
          <Card>
            <CardHeader>
              <CardTitle>Search for Riders</CardTitle>
              <CardDescription>Type rider name, Emirates ID, license number, or mobile number to find riders instantly. Searches across all records.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Search Riders</Label>
                <AsyncSelect
                  cacheOptions defaultOptions loadOptions={loadRiderOptions} value={selectedRider}
                  onChange={handleRiderChange} placeholder="Type to search riders..."
                  noOptionsMessage={({ inputValue }) => inputValue ? 'No riders found.' : 'Start typing to search...'}
                  isClearable isSearchable styles={customStyles} formatOptionLabel={formatOptionLabel}
                  menuPlacement="auto" blurInputOnSelect closeMenuOnSelect
                />
                <p className="text-xs text-slate-500">Try typing: name, Emirates ID, phone number, or plate number</p>
              </div>

              {riderInfo ? (
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="p-6 bg-gradient-to-r from-lime-50 to-green-50 rounded-lg border-2 border-lime-200" data-testid="rider-info-panel">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Rider Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoField label="Name" value={riderInfo.name} />
                      <InfoField label="Emirates ID" value={riderInfo.emirates_id} />
                      <InfoField label="License Number" value={riderInfo.license_number} />
                      <InfoField label="Mobile (UAE)" value={riderInfo.mobile_uae} />
                      <InfoField label="Platform" value={riderInfo.platform} />
                      <InfoField label="Current Bike" value={riderInfo.assigned_bike_plate || riderInfo.bike_plate_number || 'Not Assigned'} />
                      <InfoField label="Lessee Company" value={riderInfo.lessee_name || '-'} />
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Status</Label>
                        <div className="mt-1">
                          <Badge className={riderInfo.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{riderInfo.status}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  {loadingRiderDetails ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader className="w-5 h-5 animate-spin text-lime-600 mr-2" />
                      <span className="text-slate-500">Loading rider history...</span>
                    </div>
                  ) : (
                    <Tabs defaultValue="bike-history" className="w-full">
                      <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="bike-history">Bike History</TabsTrigger>
                        <TabsTrigger value="fines-history">Fines History</TabsTrigger>
                        <TabsTrigger value="documents">Documents</TabsTrigger>
                      </TabsList>

                      {/* Bike Assignment History */}
                      <TabsContent value="bike-history">
                        <Card>
                          <CardContent className="pt-4">
                            {riderVehicleHistory?.history?.length > 0 ? (
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Bike Plate</TableHead>
                                      <TableHead>Type</TableHead>
                                      <TableHead>Start Date</TableHead>
                                      <TableHead>End Date</TableHead>
                                      <TableHead>Status</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {riderVehicleHistory.history.map((entry, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-medium">{entry.bike_plate_number || entry.motorcycle_plate || '-'}</TableCell>
                                        <TableCell>{entry.type || entry.change_type || '-'}</TableCell>
                                        <TableCell>{formatDate(entry.start_date || entry.handover_date || entry.changed_at)}</TableCell>
                                        <TableCell>{formatDate(entry.end_date || entry.takeover_date) || 'Current'}</TableCell>
                                        <TableCell>
                                          <Badge className={entry.end_date || entry.takeover_date ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}>
                                            {entry.end_date || entry.takeover_date ? 'Returned' : 'Active'}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 text-center py-6">No bike assignment history found</p>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Fines History */}
                      <TabsContent value="fines-history">
                        <Card>
                          <CardContent className="pt-4">
                            {riderFinesHistory?.fines?.length > 0 ? (
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Ticket No.</TableHead>
                                      <TableHead>Date</TableHead>
                                      <TableHead>Plate</TableHead>
                                      <TableHead>Reason</TableHead>
                                      <TableHead>Amount</TableHead>
                                      <TableHead>Status</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {riderFinesHistory.fines.map((fine, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="font-mono text-xs">{fine.fine_number || fine.ticket_number || '-'}</TableCell>
                                        <TableCell>{formatDate(fine.fine_date || fine.date_of_fine)}</TableCell>
                                        <TableCell>{fine.plate_number || fine.bike_plate_number || '-'}</TableCell>
                                        <TableCell className="max-w-[200px] truncate">{fine.reason || fine.violation_type || '-'}</TableCell>
                                        <TableCell><CurrencyDisplay amount={fine.amount || fine.fine_amount || 0} /></TableCell>
                                        <TableCell>
                                          <Badge className={fine.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                            {fine.status || 'pending'}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                <div className="mt-3 text-right text-sm font-medium text-slate-700">
                                  Total Fines: <CurrencyDisplay amount={riderFinesHistory.total_amount || 0} />
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 text-center py-6">No fines history found</p>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>

                      {/* Documents */}
                      <TabsContent value="documents">
                        <Card>
                          <CardContent className="pt-4">
                            {riderDocuments?.categories ? (
                              <div className="space-y-4">
                                {Object.entries(riderDocuments.categories).map(([category, data]) => {
                                  const docs = data.documents?.filter(d => d.url) || [];
                                  if (docs.length === 0) return null;
                                  return (
                                    <div key={category}>
                                      <h4 className="text-sm font-semibold text-slate-700 mb-2">{data.label}</h4>
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {docs.map((doc, idx) => (
                                          <a
                                            key={idx}
                                            href={doc.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 p-2 bg-slate-50 rounded border hover:bg-slate-100 transition-colors text-sm"
                                            data-testid={`rider-doc-${doc.field}`}
                                          >
                                            <Eye className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                            <span className="truncate">{doc.label}</span>
                                            <ExternalLink className="w-3 h-3 text-slate-400 ml-auto flex-shrink-0" />
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                                {Object.values(riderDocuments.categories).every(cat => !cat.documents?.some(d => d.url)) && (
                                  <p className="text-sm text-slate-500 text-center py-6">No documents uploaded for this rider</p>
                                )}
                                {/* Download all button */}
                                <div className="pt-2 border-t">
                                  <a
                                    href={`${API}/riders/${riderInfo.id}/download-documents`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                                    data-testid="download-all-rider-docs"
                                  >
                                    <Download className="w-4 h-4" />
                                    Download All Documents (ZIP)
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 text-center py-6">No documents found</p>
                            )}
                          </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                  )}
                </div>
              ) : (
                <EmptyState message="Select a rider from the dropdown to see auto-filled information" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== MOTORCYCLES TAB ===== */}
        <TabsContent value="motorcycle">
          <Card>
            <CardHeader>
              <CardTitle>Search for Motorcycles</CardTitle>
              <CardDescription>Type plate number, model, chassis number, or assigned rider/lessee to find motorcycles. Searches across all records.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Search Motorcycles</Label>
                <AsyncSelect
                  cacheOptions defaultOptions loadOptions={loadMotorcycleOptions} value={selectedMotorcycle}
                  onChange={handleMotorcycleChange} placeholder="Type to search motorcycles..."
                  noOptionsMessage={({ inputValue }) => inputValue ? 'No motorcycles found.' : 'Start typing to search...'}
                  isClearable isSearchable styles={customStyles} formatOptionLabel={formatOptionLabel}
                  menuPlacement="auto" blurInputOnSelect closeMenuOnSelect
                />
                <p className="text-xs text-slate-500">Try typing: plate number, model name, rider name, or lessee name</p>
              </div>

              {motorcycleInfo ? (
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200" data-testid="motorcycle-info-panel">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Motorcycle Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InfoField label="Plate Number" value={motorcycleInfo.plate_number} />
                      <InfoField label="Model" value={`${motorcycleInfo.model_name || ''} ${motorcycleInfo.model_version || ''}`.trim()} />
                      <InfoField label="Chassis Number" value={motorcycleInfo.chassis_number} />
                      <InfoField label="Color" value={motorcycleInfo.color} />
                      <InfoField label="City" value={motorcycleInfo.city} />
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Status</Label>
                        <div className="mt-1"><Badge>{motorcycleInfo.status}</Badge></div>
                      </div>
                      <InfoField label="Assigned Rider" value={motorcycleInfo.assigned_rider_name || motorcycleInfo.rider_name || 'Unassigned'} />
                      <InfoField label="Lessee Company" value={motorcycleInfo.lessee_name || '-'} />
                      <InfoField label="Registration Expiry" value={formatDate(motorcycleInfo.registration_expiry_date)} />
                      <InfoField label="Insurance Expiry" value={formatDate(motorcycleInfo.insurance_expiry_date)} />
                      <InfoField label="Insurance Provider" value={motorcycleInfo.insurance_provider} />
                      <InfoField label="Lease Status" value={motorcycleInfo.lease_status} />
                    </div>
                    {/* Registration Card */}
                    {motorcycleInfo.registration_card_url && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <a
                          href={motorcycleInfo.registration_card_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm text-blue-700 hover:bg-blue-50 transition-colors"
                          data-testid="view-registration-card"
                        >
                          <Eye className="w-4 h-4" />
                          View Registration Card
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>

                  {/* History Tabs */}
                  {loadingMotorcycleHistory ? (
                    <div className="flex items-center justify-center p-8">
                      <Loader className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                      <span className="text-slate-500">Loading motorcycle history...</span>
                    </div>
                  ) : motorcycleHistory ? (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryCard label="Maintenance" value={motorcycleHistory.maintenance_history?.length || 0} sub="Records" color="blue" />
                        <SummaryCard label="Maintenance Cost" value={<CurrencyDisplay amount={motorcycleHistory.total_maintenance_cost || 0} />} sub="Total spent" color="orange" />
                        <SummaryCard label="Total Fines" value={motorcycleHistory.fines_history?.length || 0} sub="Violations" color="red" />
                        <SummaryCard label="Fines Amount" value={<CurrencyDisplay amount={motorcycleHistory.total_fines_amount || 0} />} sub="Total fines" color="purple" />
                      </div>

                      <Tabs defaultValue="rider-assignments" className="w-full">
                        <TabsList className="grid w-full grid-cols-3">
                          <TabsTrigger value="rider-assignments">Rider Assignments</TabsTrigger>
                          <TabsTrigger value="fines-history">Fines History</TabsTrigger>
                          <TabsTrigger value="maintenance-history">Maintenance History</TabsTrigger>
                        </TabsList>

                        {/* Rider Assignments */}
                        <TabsContent value="rider-assignments">
                          <Card>
                            <CardContent className="pt-4">
                              {motorcycleHistory.rider_assignments?.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Rider</TableHead>
                                        <TableHead>Start Date</TableHead>
                                        <TableHead>End Date</TableHead>
                                        <TableHead>Status</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {motorcycleHistory.rider_assignments.map((a, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-medium">{a.rider_name || '-'}</TableCell>
                                          <TableCell>{formatDate(a.start_date)}</TableCell>
                                          <TableCell>{a.end_date ? formatDate(a.end_date) : '-'}</TableCell>
                                          <TableCell>
                                            <Badge className={a.status === 'Currently Assigned' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                              {a.status}
                                            </Badge>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500 text-center py-6">No rider assignments found</p>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>

                        {/* Fines History */}
                        <TabsContent value="fines-history">
                          <Card>
                            <CardContent className="pt-4">
                              {motorcycleHistory.fines_history?.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Ticket No.</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Reason</TableHead>
                                        <TableHead>Amount</TableHead>
                                        <TableHead>Status</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {motorcycleHistory.fines_history.map((fine, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="font-mono text-xs">{fine.fine_number || fine.ticket_number || '-'}</TableCell>
                                          <TableCell>{formatDate(fine.fine_date || fine.date_of_fine)}</TableCell>
                                          <TableCell className="max-w-[200px] truncate">{fine.reason || fine.violation_type || '-'}</TableCell>
                                          <TableCell><CurrencyDisplay amount={fine.amount || fine.fine_amount || 0} /></TableCell>
                                          <TableCell>
                                            <Badge className={fine.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{fine.status || 'pending'}</Badge>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500 text-center py-6">No fines found for this motorcycle</p>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>

                        {/* Maintenance History */}
                        <TabsContent value="maintenance-history">
                          <Card>
                            <CardContent className="pt-4">
                              {motorcycleHistory.maintenance_history?.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Technician</TableHead>
                                        <TableHead>Items</TableHead>
                                        <TableHead>Cost</TableHead>
                                        <TableHead>Status</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {motorcycleHistory.maintenance_history.map((m, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell>{formatDate(m.maintenance_date)}</TableCell>
                                          <TableCell className="capitalize">{m.type || '-'}</TableCell>
                                          <TableCell>{m.technician || '-'}</TableCell>
                                          <TableCell>{m.repair_items?.length || 0} items</TableCell>
                                          <TableCell><CurrencyDisplay amount={m.total_cost || 0} /></TableCell>
                                          <TableCell>
                                            <Badge className={
                                              m.status === 'completed' ? 'bg-green-100 text-green-800' :
                                              m.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                                            }>{m.status || '-'}</Badge>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500 text-center py-6">No maintenance records found</p>
                              )}
                            </CardContent>
                          </Card>
                        </TabsContent>
                      </Tabs>
                    </>
                  ) : null}
                </div>
              ) : (
                <EmptyState message="Select a motorcycle from the dropdown to see complete information" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== LESSEES TAB ===== */}
        <TabsContent value="lessee">
          <Card>
            <CardHeader>
              <CardTitle>Search for Lessee Companies</CardTitle>
              <CardDescription>Type company name, owner name, mobile, trade license, or TRN to find lessees</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Search Lessee Companies</Label>
                <SearchableSelect
                  options={lesseeOptions} value={selectedLessee} onValueChange={handleLesseeChange}
                  placeholder="Type to search lessees..." emptyMessage="No lessees found." className="w-full"
                />
                <p className="text-xs text-slate-500">Try typing: company name, owner name, mobile number, or license number</p>
              </div>

              {lesseeInfo ? (
                <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200" data-testid="lessee-info-panel">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Lessee Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoField label="Company Name" value={lesseeInfo.company_name} />
                    <InfoField label="Owner Name" value={lesseeInfo.owner_name} />
                    <InfoField label="Mobile" value={lesseeInfo.owner_mobile || lesseeInfo.contact_mobile} />
                    <InfoField label="Email" value={lesseeInfo.owner_email || lesseeInfo.contact_email} />
                    <InfoField label="Trade License" value={lesseeInfo.trade_license_number || lesseeInfo.trade_license_no} />
                    <InfoField label="TRN" value={lesseeInfo.trn_number || lesseeInfo.trn_no} />
                    <div className="col-span-2">
                      <InfoField label="Address" value={lesseeInfo.address} />
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState message="Select a lessee from the dropdown to see auto-filled information" />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Usage Instructions */}
      <Card className="mt-6 bg-slate-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-slate-800 mb-3">How to Use Search:</h3>
          <div className="space-y-2 text-sm text-slate-600">
            <Step num={1} text={<><strong>Select Tab:</strong> Choose whether to search Riders, Motorcycles, or Lessees</>} />
            <Step num={2} text={<><strong>Click Search Box:</strong> Click on the search dropdown to open it</>} />
            <Step num={3} text={<><strong>Type to Search:</strong> Start typing any related information. Results are fetched from the entire database.</>} />
            <Step num={4} text={<><strong>Select & View:</strong> Click any result to view complete information including history, fines, and documents</>} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Small reusable components
const InfoField = ({ label, value }) => (
  <div>
    <Label className="text-sm font-medium text-slate-600">{label}</Label>
    <Input value={value || '-'} readOnly className="mt-1 bg-white" />
  </div>
);

const EmptyState = ({ message }) => (
  <div className="p-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 text-center">
    <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
    <p className="text-slate-600">{message}</p>
  </div>
);

const Step = ({ num, text }) => (
  <div className="flex items-start gap-2">
    <div className="w-6 h-6 rounded-full bg-lime-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">{num}</div>
    <p>{text}</p>
  </div>
);

const SummaryCard = ({ label, value, sub, color }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200',
    orange: 'bg-orange-50 border-orange-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200'
  };
  return (
    <Card className={`${colors[color] || 'bg-slate-50'}`}>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-slate-500 mb-1">{label}</p>
        <div className="text-xl font-bold text-slate-800">{value}</div>
        <p className="text-xs text-slate-500">{sub}</p>
      </CardContent>
    </Card>
  );
};

export default SearchableDropdownDemo;
